import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { RegistrationService } from '../services/registration.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private authService: AuthService,
    private registrationService: RegistrationService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Add ?_t=timestamp (or &_t=timestamp) so every request gets fresh data from API (no cache)
    const timestamp = new Date().getTime();
    const url = request.url.includes('?')
      ? `${request.url}&_t=${timestamp}`
      : `${request.url}?_t=${timestamp}`;

    // Add no-cache headers so mobile/browser always gets latest data from API
    request = request.clone({
      url,
      setHeaders: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    // Skip adding token for login and register endpoints
    if (request.url.includes('/Account/login') || request.url.includes('/Account/register')) {
      return next.handle(request);
    }

    // Add access token to request
    const accessToken = this.authService.getAccessToken();
    if (accessToken) {
      request = this.addTokenToRequest(request, accessToken);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // If 401 and we have a refresh token, try to refresh
        if (error.status === 401 && this.authService.getRefreshToken()) {
          return this.handle401Error(request, next);
        }

        // If refresh failed or no refresh token, logout
        if (error.status === 401) {
          this.handleLogout();
        }

        return throwError(() => error);
      })
    );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.authService.getRefreshToken();
      if (refreshToken) {
        return this.registrationService.refreshToken(refreshToken).pipe(
          switchMap((response: any) => {
            this.isRefreshing = false;
            
            if (response.success && response.accessToken && response.refreshToken) {
              this.authService.setTokens(response.accessToken, response.refreshToken);
              this.refreshTokenSubject.next(response.accessToken);
              return next.handle(this.addTokenToRequest(request, response.accessToken));
            } else {
              this.handleLogout();
              return throwError(() => new Error('Token refresh failed'));
            }
          }),
          catchError((error) => {
            this.isRefreshing = false;
            this.handleLogout();
            return throwError(() => error);
          })
        );
      }
    }

    // If already refreshing, wait for the new token
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenToRequest(request, token)))
    );
  }

  private handleLogout(): void {
    this.authService.clearAll();
    this.router.navigate(['/home'], { replaceUrl: true });
  }
}

